import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { DeclarationRef, fetchDeclaration, metaLine } from '../api/declarations'
import { SessionState } from '../state/types'
import { draftIdsFromTimeline } from '../util/drafts'
import { editorPath } from '../util/openUrl'

interface OutcomeBarProps { session: SessionState }

/**
 * The run outcome, pinned under the header so the draft and "Open in editor" never need scrolling to.
 *
 * `session_complete` carries the id and openUrl. The declaration type, offices, identifier and
 * status are not on the wire, so they come from the declaration itself — the client already holds the
 * operator's token for that call. A failed fetch degrades to the ref-only bar rather than hiding it.
 */
export function OutcomeBar({ session }: Readonly<OutcomeBarProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [ref, setRef] = useState<DeclarationRef | null>(null)
  const { result } = session
  const id = result?.transitOperationId ?? 0

  useEffect(() => {
    // Reset unconditionally, before the early return — otherwise switching from one completed
    // session to another (positive id -> positive id) would leave the previous draft's status
    // and meta line on screen, mismatched against the new #id, until its own fetch resolves.
    setRef(null)
    if (id <= 0) return undefined
    const controller = new AbortController()
    let live = true
    fetchDeclaration(id, controller.signal)
      .then((d) => { if (live) setRef(d) })
      .catch(() => { if (live) setRef(null) }) // the bar stands on its own without these fields
    return () => { live = false; controller.abort() }
  }, [id])

  if (!result) return null
  const path = editorPath(result.openUrl ?? '')
  // A run can produce several drafts (one T1 per wagon, or two identical declarations on request)
  // while session_complete names only one — recover the rest from the timeline so every draft is
  // reachable by click, not just mentioned in the prose.
  const draftIds = draftIdsFromTimeline(session.timeline, id)

  return (
    <div className="copilot-outcome" data-testid="copilot-outcome">
      <div className="copilot-outcome__ident">
        <div className="copilot-outcome__line">
          {id > 0
            ? <span className="copilot-outcome__ref" data-testid="copilot-draft-ref">{`#${id}`}</span>
            : <span className="copilot-outcome__ref">{t('copilot.complete.done')}</span>}
          {ref?.status && (
            <span className="copilot-outcome__tag" data-testid="copilot-outcome-status">{ref.status}</span>
          )}
        </div>
        {ref && (
          <div className="copilot-outcome__meta" data-testid="copilot-outcome-meta">{metaLine(ref)}</div>
        )}
        {draftIds.length > 1 && (
          <div className="copilot-outcome__drafts" data-testid="copilot-outcome-drafts">
            <span className="copilot-outcome__label">
              {t('copilot.complete.drafts', { count: draftIds.length })}
            </span>
            {draftIds.map((draftId) => (
              <button
                key={draftId}
                type="button"
                className="copilot-outcome__draft"
                data-testid={`copilot-open-draft-${draftId}`}
                onClick={() => navigate(`/transit-operation/${draftId}`)}
              >
                {`#${draftId}`}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="copilot-outcome__metrics">
        {id > 0 && (
          <button
            type="button"
            className="btn btn-primary btn-sm copilot-outcome__open"
            disabled={!path}
            data-testid="copilot-open"
            onClick={() => path && navigate(path)}
          >
            {t('copilot.complete.open')}
            <i className="fa fa-arrow-right" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
